import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X, Undo2, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_LABEL = { auto_applied: "AI added", approved: "Approved", dismissed: "Dismissed", undone: "Undone" };
const STATUS_CLS = {
  auto_applied: "bg-indigo-50 text-indigo-600", approved: "bg-green-50 text-green-600",
  dismissed: "bg-slate-100 text-slate-500", undone: "bg-slate-100 text-slate-500",
};

function ago(iso) {
  if (!iso) return "never";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
}

function Proposal({ p, canEdit, busy, onApprove, onDismiss }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-100 p-3" data-testid={`proposal-${p.term}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">{p.term}</span>
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
              {Math.round(p.confidence * 100)}% {p.source === "ai" ? "AI" : "pattern"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{p.reason}</p>
          <p className="text-[11px] text-slate-400 mt-1">
            In {p.flagged_count} flagged · {p.passed_count} kept
            {p.evidence?.length > 0 && (
              <button type="button" onClick={() => setOpen(!open)} className="ml-2 inline-flex items-center gap-0.5 text-indigo-600">
                evidence {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-1.5 shrink-0">
            <button type="button" disabled={busy} onClick={() => onApprove(p)} data-testid={`approve-${p.term}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 text-xs font-semibold disabled:opacity-60">
              <Check className="w-3.5 h-3.5" /> Add
            </button>
            <button type="button" disabled={busy} onClick={() => onDismiss(p)} data-testid={`dismiss-${p.term}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold disabled:opacity-60">
              <X className="w-3.5 h-3.5" /> Dismiss
            </button>
          </div>
        )}
      </div>
      {open && (
        <ul className="mt-2 space-y-1">
          {p.evidence.map((e, i) => (
            <li key={e.comment_id || i} className="text-xs text-slate-500 border-l-2 border-slate-200 pl-2">{e.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "What the AI changed": learn-now, pending keyword proposals, and the log of changes with undo. */
export default function LearningPanel({ accountId, canEdit, onRulesChange }) {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    api.get(`/accounts/${accountId}/learning`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [accountId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const learnNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post(`/accounts/${accountId}/learn-now`);
      if (r.skipped) toast.info("Already running");
      else if (r.auto_applied) toast.success(`${r.created} new, ${r.auto_applied} added automatically`);
      else toast.success(r.created ? `${r.created} new suggestion${r.created > 1 ? "s" : ""}` : "Nothing new to learn");
      if (r.auto_applied) onRulesChange?.();
      load();
    } catch (e) { fail(e); } finally { setRunning(false); }
  };

  const act = async (p, verb, message, adds) => {
    setBusyId(p.id);
    try {
      await api.post(`/rule-proposals/${p.id}/${verb}`);
      if (adds !== undefined) onRulesChange?.(p.term, adds);
      toast.success(message);
      load();
    } catch (e) { fail(e); load(); } finally { setBusyId(""); }
  };

  const toggleAuto = async () => {
    try {
      await api.put(`/accounts/${accountId}/learning`, { auto_apply: !data.auto_apply });
      setData({ ...data, auto_apply: !data.auto_apply });
    } catch (e) { fail(e); }
  };

  return (
    <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-5" data-testid="learning-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Sparkles className="w-4 h-4 text-indigo-600" /> What the AI changed
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Learns from comments you flag as missed. Last learned: {ago(data?.last_run_at)}.
          </p>
        </div>
        {canEdit && (
          <button type="button" onClick={learnNow} disabled={running} data-testid="learn-now"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Learn now
          </button>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-600 animate-spin" /></div>
      ) : (
        <>
          <label className="flex items-start gap-3 text-xs text-slate-600">
            <input type="checkbox" checked={data.auto_apply} disabled={!canEdit} onChange={toggleAuto}
              data-testid="auto-apply-toggle" className="mt-0.5 accent-indigo-600" />
            <span>
              <span className="font-semibold text-slate-700">Add very certain keywords automatically.</span>{" "}
              Only AI suggestions with 95%+ confidence, in 3+ flagged comments and none that were kept. Always listed
              below, and you can undo them.
            </span>
          </label>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-600">Waiting for you ({data.pending.length})</p>
            {data.pending.length === 0 && <p className="text-xs text-slate-400">No suggestions right now.</p>}
            {data.pending.map((p) => (
              <Proposal key={p.id} p={p} canEdit={canEdit} busy={busyId === p.id}
                onApprove={(x) => act(x, "approve", `Added "${x.term}"`, true)}
                onDismiss={(x) => act(x, "dismiss", "Dismissed")} />
            ))}
          </div>

          {data.recent.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-600">Recent changes</p>
              {data.recent.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 text-xs" data-testid={`recent-${p.term}`}>
                  <div className="min-w-0">
                    <span className={`px-1.5 py-0.5 rounded font-medium mr-2 ${STATUS_CLS[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                    <span className="font-semibold text-slate-700">{p.term}</span>
                    <span className="text-slate-400"> · {ago(p.resolved_at)}</span>
                  </div>
                  {canEdit && (p.status === "auto_applied" || p.status === "approved") && (
                    <button type="button" disabled={busyId === p.id} onClick={() => act(p, "undo", `Removed "${p.term}"`, false)}
                      data-testid={`undo-${p.term}`}
                      className="inline-flex items-center gap-1 text-indigo-600 font-semibold shrink-0 disabled:opacity-60">
                      <Undo2 className="w-3.5 h-3.5" /> Undo
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
